import React, { useState, useMemo, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Button, TableAdv, showToast, Alert } from "../../../../../components";
import { 
  addIncentivesBulk, 
  fetchUsers, 
  updateIncentive, 
  deleteIncentive 
} from "../../../../../redux/slices/incentives/incentivesApi";
import { 
  getIncentivesStart, 
  getIncentivesFailure 
} from "../../../../../redux/slices/incentives/incentiveSlice";
import { fetchIncentiveSettingsApi } from "../../../../../redux/slices/incentives/incentiveSettingApi";

const MODULE_MAP = {
  "All": 1,
  "ATS": 2,
  "Workforce": 3,
  "Bench": 4
};



const SETTINGS_KEY_MAP = {
  "Bench": "INCENTIVE_BENCH",
  "IT": "INCENTIVE_IT",
  "NonIT": "INCENTIVE_NON_IT"
};

const TableIncentive = forwardRef(({ 
  type, 
  module, 
  roleId,
  availableRoles: parentRoles, 
  rolesLoaded: parentRolesLoaded 
}, ref) => {
  const dispatch = useDispatch();

  // Pagination State
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Side Panel State
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rawUsers, setRawUsers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Multi-Row Edit State
  const [editingRows, setEditingRows] = useState(new Set());
  const [pendingChanges, setPendingChanges] = useState({});
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null, name: "" });

  // Settings State
  const [defaultSettings, setDefaultSettings] = useState({});

  // Refs for preventing unnecessary operations
  const prevModuleRef = useRef(module);
  const fetchingUsersRef = useRef(false);

  // Memoized cache key
  const cacheKey = useMemo(
    () => `${module}_${String(type).replace(/\s+/g, '')}_${page}_${rowsPerPage}`,
    [module, type, page, rowsPerPage]
  );

  // Redux selectors
  const { incentivesByKey, loading } = useSelector((state) => state.incentive);
  const user = useSelector((state) => state.auth.user);

  // Memoized user data extraction
  const currentUserId = useMemo(() => {
    return user?.uiid || user?.data?.uiid;
  }, [user]);

  // Cached data retrieval
  const cachedData = useMemo(() => incentivesByKey[cacheKey] || {}, [incentivesByKey, cacheKey]);
  const incentives = cachedData.data || [];
  const reduxTotalCount = cachedData.totalCount || 0;

  // Memoized total count calculation
  const safeTotalCount = useMemo(() => {
    if (typeof reduxTotalCount === 'number') return reduxTotalCount;
    if (typeof reduxTotalCount === 'string') return Number(reduxTotalCount) || 0;
    if (typeof reduxTotalCount === 'object' && reduxTotalCount !== null) {
      return Number(reduxTotalCount.data || reduxTotalCount.count || 0);
    }
    return 0;
  }, [reduxTotalCount]);

  // Memoized role ID resolver
  const getRoleId = useCallback(() => {
    if (roleId) return roleId;
    if (!parentRolesLoaded || !parentRoles) return null;
    
    const nameMatch = parentRoles.find(r => r.name === type);
    return nameMatch?.uiid || null;
  }, [roleId, type, parentRoles, parentRolesLoaded]);

  // Optimized settings fetch - only when module or orgId changes
  useEffect(() => {
    // Skip if module hasn't changed and we already have settings
    if (prevModuleRef.current === module && Object.keys(defaultSettings).length > 0) {
      return;
    }
    
    prevModuleRef.current = module;

    const key = SETTINGS_KEY_MAP[module];
    
    if (!key) return;

    fetchIncentiveSettingsApi(key)
      .then(data => {
        let settings = null;
        
        if (Array.isArray(data)) {
          settings = data[0];
        } else if (data.data) {
          settings = Array.isArray(data.data) ? data.data[0] : data.data;
        } else {
          settings = data;
        }
        
        if (settings) {
          const valObj = settings.value || {};
          const qTarget = Number(settings.quarterlyTarget || 0);
          const yTarget = Number(settings.yearlyTarget || 0);
          const generalPct = Number(valObj.generalPct || settings.generalPct || 0);

          setDefaultSettings({
            ...settings,
            ...valObj,
            quarterlyTarget: qTarget,
            yearlyTarget: yTarget,
            incentiveType: valObj.incentiveType || (module === 'Bench' ? 'flat' : 'percentage'),
            generalPct,
            q1Pct: Number(valObj.q1Pct || settings.q1Pct || 0),
            fiscalYearPct: Number(valObj.fiscalYearPct || settings.fiscalYearPct || 0)
          });
        }
      })
      .catch(err => console.warn("Failed to fetch default settings for", module, err));
  }, [module]); // Removed defaultSettings from deps to prevent loops

  // Optimized data loading
  const loadData = useCallback(() => {
    if (!parentRolesLoaded && typeof type !== 'number') return;
    
    const targetRoleId = getRoleId();
    const filters = {};

    if (module !== 'All') {
      filters.module = { type: "NONE", value: module };
    }
    
    if (targetRoleId) {
      filters.role = { type: "NONE", value: targetRoleId };
    } else {
      console.warn("[TableIncentive] Role Not Found:", type);
      dispatch(getIncentivesFailure({ key: cacheKey, error: "Role Not Found" }));
      return;
    }

    // No organizationId filter needed — Workforce DB is already org-scoped
    
    dispatch(getIncentivesStart({
      key: cacheKey,
      limit: rowsPerPage,
      page: page,
      filters: filters,
      sort: { _id: -1 },
      incl: ["role"],
    }));
  }, [type, module, getRoleId, parentRolesLoaded, dispatch, cacheKey, page, rowsPerPage]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Memoized formatted rows
  const rows = useMemo(() => {
    const rawData = Array.isArray(incentives) ? incentives : (incentives?.data || []);
    if (!Array.isArray(rawData)) return [];
    
    return rawData.map((item) => {
      const isBench = item.module === 'Bench';
      const isFlat = defaultSettings.incentiveType === 'flat' || isBench;

      return {
        id: item.uiid || item._id,
        ...item,
        name: item.targetUserName || "Unknown User",
        general_pct: isBench ? "0%" : (isFlat ? `$ ${item.generalPct || 0}` : `${item.generalPct || 0}%`),
        q1_pct: isBench ? "0%" : (isFlat ? `$ ${item.q1Pct || 0}` : `${item.q1Pct || 0}%`),
        fiscal_year_pct: isBench ? "0%" : (isFlat ? `$ ${item.fiscalYearPct || 0}` : `${item.fiscalYearPct || 0}%`),
        fixed_amount: `$ ${item.fixedAmount || 0}`,
      };
    });
  }, [incentives, defaultSettings.incentiveType]);

  // Optimized user fetching - only when panel opens
  useEffect(() => {
    if (!showAddMember || !parentRolesLoaded || fetchingUsersRef.current) return;

    const fetchCandidates = async () => {
      fetchingUsersRef.current = true;
      setLoadingMembers(true);
      
      try {
        const targetRoleId = getRoleId();
        if (!targetRoleId) {
          console.warn("Could not resolve Role ID for:", type, "Module:", module);
          setRawUsers([]);
          return;
        }

        // Determine which DB(s) to query based on the role's module.
        // module 2 = ATS only, module 3 = WF only, module 4 = Bench only
        // module 1 = All/Global — userrefs synced to ALL DBs, query based on incentive module context
        const currentRole = parentRoles?.find(r => r.uiid === targetRoleId);
        const roleModule = currentRole?.module;

        const commonFilters = {
          limit: 100, page: 1,
          filters: { "role.uiid": { type: "NONE", value: targetRoleId } },
          incl: ["role"]
        };



        let allUsers = [];

        if (roleModule === 1) {
          // Global role — fetch from multiple DBs based on incentive module
          const sourcesToQuery = module === "Bench" ? ["bench"] : ["ats", "wf"];
          const results = await Promise.allSettled(
            sourcesToQuery.map(src => fetchUsers({ ...commonFilters, source: src }))
          );
          results.forEach(r => {
            if (r.status === "fulfilled") {
              const d = r.value.data;
              const list = Array.isArray(d) ? d : d?.data || [];
              allUsers.push(...list);
            }
          });
        } else {
          // Specific module role — query single DB
          const moduleSourceMap = { 2: "ats", 3: "wf", 4: "bench" };
          const source = moduleSourceMap[roleModule] || "ats";
          const { data } = await fetchUsers({ ...commonFilters, source });
          allUsers = Array.isArray(data) ? data : data?.data || [];
        }

        // Deduplicate by uiid (same user may exist in multiple DBs)
        const seen = new Set();
        const userList = allUsers.filter(u => {
          if (seen.has(u.uiid)) return false;
          seen.add(u.uiid);
          return true;
        });
        setRawUsers(userList);
      } catch (err) {
        console.error("Error fetching candidates", err);
      } finally {
        setLoadingMembers(false);
        fetchingUsersRef.current = false;
      }
    };

    fetchCandidates();
  }, [showAddMember, type, getRoleId, module, parentRolesLoaded]);

  // Memoized potential members derivation
  const allPotentialMembers = useMemo(() => {
    const existingUserIds = new Set(rows.map(r => String(r.targetUserId)));
    const targetRoleId = getRoleId();

    return rawUsers
      .filter(u => {
        // Exclude admins
        if (u.role?.type === 1 || u.role === 1) return false;
        
        // Check duplicates using Set for O(1) lookup
        if (existingUserIds.has(String(u.uiid))) return false;
        
        // No organizationId check needed — userrefs are already org-scoped
        
        // Role match
        const userRoleId = u.role?.uiid || u.role?.type || u.role;
        return userRoleId === targetRoleId;
      })
      .map(u => ({
        id: u.uiid,
        name: `${u.firstName} ${u.lastName}`,
        role: u.roleName || type,
        fullUser: u
      }));
  }, [rawUsers, rows, getRoleId, type]);

  // Memoized filtered members
  const availableMembers = useMemo(() => {
    if (!searchTerm) return allPotentialMembers;
    const lowerSearch = searchTerm.toLowerCase();
    return allPotentialMembers.filter(m => 
      m.name.toLowerCase().includes(lowerSearch)
    );
  }, [allPotentialMembers, searchTerm]);

  // Optimized handlers with useCallback
  const handleMoveMembers = useCallback(async () => {
    if (selectedNewMembers.length === 0) return;
    
    setLoadingMembers(true);
    try {
      const membersToAdd = allPotentialMembers.filter(m => 
        selectedNewMembers.includes(m.id)
      );
      
      const incentivesPayload = membersToAdd.map(member => ({
        targetUserId: member.id,
        targetUserName: member.name,
        role: getRoleId(),
        module: module,
        addedBy: currentUserId,
        generalPct: Number(defaultSettings.generalPct || 0),
        q1Pct: Number(defaultSettings.q1Pct || 0),
        fiscalYearPct: Number(defaultSettings.fiscalYearPct || 0),
        fixedAmount: 0
      }));

      await addIncentivesBulk(incentivesPayload);
      showToast(`${membersToAdd.length} member(s) added successfully`, "success");

      loadData();
      // setShowAddMember(false); // Keep panel open as per user request
      setSelectedNewMembers([]);
      // setRawUsers([]); // Don't clear users to allow adding more from the same search/list
    } catch (err) {
      console.error("Failed to add members", err);
      showToast("Failed to add members. Please try again.", "error");
    } finally {
      setLoadingMembers(false);
    }
  }, [selectedNewMembers, allPotentialMembers, module, currentUserId, getRoleId, defaultSettings, loadData]);

  const handleToggleMember = useCallback((id) => {
    setSelectedNewMembers(prev =>
      prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id]
    );
  }, []);

  const handleDelete = useCallback(async () => {
    const { id, name } = deleteModal;
    if (!id) return;
    
    try {
      await deleteIncentive({ incentiveUiid: id });
      showToast(`Successfully removed ${name} from the list`, "success");
      loadData();
    } catch (error) {
      console.error("Failed to delete incentive:", error);
      showToast("Failed to remove member", "error");
    } finally {
      setDeleteModal({ isOpen: false, id: null, name: "" });
    }
  }, [deleteModal, loadData]);

  const handleEditClick = useCallback((row) => {
    setEditingRows(prev => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
        // Clean up pending changes if we're canceling edit for this row
        setPendingChanges(old => {
          const updated = { ...old };
          delete updated[row.id];
          return updated;
        });
      } else {
        next.add(row.id);
        setPendingChanges(old => ({
          ...old,
          [row.id]: {
            generalPct: row.generalPct || 0,
            q1Pct: row.q1Pct || 0,
            fiscalYearPct: row.fiscalYearPct || 0,
            fixedAmount: row.fixedAmount || 0
          }
        }));
      }
      return next;
    });
  }, []);

  const handleInputChange = useCallback((rowId, field, value) => {
    setPendingChanges(prev => {
      const current = prev[rowId] || {};
      const numericValue = Number(value);
      
      let updatedData = { ...current, [field]: value };

      // Mutual exclusivity logic
      if (field === 'fixedAmount' && numericValue > 0) {
        updatedData = {
          ...updatedData,
          generalPct: 0,
          q1Pct: 0,
          fiscalYearPct: 0
        };
      } else if (['generalPct', 'q1Pct', 'fiscalYearPct'].includes(field) && numericValue > 0) {
        updatedData = {
          ...updatedData,
          fixedAmount: 0
        };
      }

      return { ...prev, [rowId]: updatedData };
    });
  }, []);

  const handleBulkSave = useCallback(async () => {
    const changedIds = Object.keys(pendingChanges);
    if (changedIds.length === 0) {
      showToast("All changes are up to date", "success");
      return true;
    }

    setIsBulkSaving(true);
    try {
      const updatePromises = changedIds.map(async (id) => {
        const changes = pendingChanges[id];
        return updateIncentive({
          uiid: id,
          payload: [{
            type: "SET",
            value: {
              generalPct: Number(changes.generalPct),
              q1Pct: Number(changes.q1Pct),
              fiscalYearPct: Number(changes.fiscalYearPct),
              fixedAmount: Number(changes.fixedAmount)
            }
          }]
        });
      });

      await Promise.all(updatePromises);
      showToast(`${changedIds.length} records updated successfully`, "success");
      
      setEditingRows(new Set());
      setPendingChanges({});
      loadData();
      return true;
    } catch (error) {
      console.error("Bulk save failed:", error);
      showToast("Failed to save some changes", "error");
      return false;
    } finally {
      setIsBulkSaving(false);
    }
  }, [pendingChanges, loadData]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    handleBulkSave,
    isDirty: Object.keys(pendingChanges).length > 0,
    isSaving: isBulkSaving
  }), [handleBulkSave, pendingChanges, isBulkSaving]);



  const handlePaginationChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    setRowsPerPage(newLimit);
    setPage(1);
  }, []);

  const toggleAddMemberPanel = useCallback(() => {
    setShowAddMember(prev => !prev);
  }, []);

  // Memoized render function for editable cells
  const renderEditableCell = useCallback((row, field, formField) => {
    if (!editingRows.has(row.id)) return row[field];
    
    const rowChanges = pendingChanges[row.id] || {};
    const value = rowChanges[formField] ?? row[formField];

    return (
      <input 
        type="number" 
        className="w-full p-1 border border-orange-200 rounded text-sm focus:border-orange-500 outline-none"
        value={value}
        onChange={(e) => handleInputChange(row.id, formField, e.target.value)}
      />
    );
  }, [editingRows, pendingChanges, handleInputChange]);

  // Memoized columns
  const columns = useMemo(() => {
    const isFlat = defaultSettings.incentiveType === 'flat';
    const unit = isFlat ? "" : " (%)";

    return [
      { key: "name", label: "Name", width: "200px" },
      { 
        key: "general_pct", 
        label: `General Rate${unit}`, 
        width: "120px",
        render: (row) => renderEditableCell(row, "general_pct", "generalPct")
      },
      { 
        key: "q1_pct", 
        label: `Q1 Rate${unit}`, 
        width: "100px",
        render: (row) => renderEditableCell(row, "q1_pct", "q1Pct")
      },
      { 
        key: "fiscal_year_pct", 
        label: `Fiscal Year Rate${unit}`, 
        width: "150px",
        render: (row) => renderEditableCell(row, "fiscal_year_pct", "fiscalYearPct")
      },
      { 
        key: "fixed_amount", 
        label: "Fixed Amount", 
        width: "150px",
        render: (row) => renderEditableCell(row, "fixed_amount", "fixedAmount")
      },
    {
      key: "action",
      label: "Action",
      width: "60px",
      render: (row) => (
        <div className="flex items-center gap-1">
          <Button
            icon={editingRows.has(row.id) ? "edit_off" : "edit"}
            variant="text"
            size="sm"
            className={editingRows.has(row.id) ? "!text-orange-500 hover:!bg-orange-50 !p-1" : "!text-blue-400 hover:!text-blue-600 !p-1"}
            onClick={() => handleEditClick(row)}
            title={editingRows.has(row.id) ? "Stop Editing" : "Edit"}
          />
          <Button
            icon="delete"
            variant="text"
            size="sm"
            className="!text-gray-400 hover:!text-red-500 !p-1"
            onClick={() => setDeleteModal({ isOpen: true, id: row.id, name: row.name })}
            title="Delete"
          />
        </div>
      ),
    },
  ];
}, [editingRows, renderEditableCell, handleEditClick, defaultSettings.incentiveType]);

  // Memoized toolbar
  const toolbarRight = useMemo(() => ({
    search: true,
    add: [{
      component: (
        <Button
          title={showAddMember ? "Close Panel" : "Add Member"}
          icon={showAddMember ? "close" : "add"}
          size="sm"
          variant={showAddMember ? "outlined" : "contained"}
          className={
            showAddMember
              ? "!border-red-500 !text-red-500 !bg-white hover:!bg-red-50"
              : "!bg-orange-500 !text-white hover:!bg-orange-600"
          }
          onClick={toggleAddMemberPanel}
        />
      ),
    }],
    order: ["search", "add"],
  }), [showAddMember, toggleAddMemberPanel]);

  return (
    <div className="h-full w-full flex flex-col xl:flex-row overflow-hidden gap-4 relative">
      {/* Side Panel */}
      {showAddMember && (
        <div className="absolute inset-0 z-50 bg-white w-full h-full overflow-hidden flex flex-col p-2 xl:p-0 xl:static xl:w-[25%] xl:min-w-[250px] xl:border-r border-gray-200 xl:pr-2">
          {/* Mobile Header */}
          <div className="flex justify-between items-center mb-2 xl:hidden">
            <h3 className="font-semibold text-md text-gray-800">Add Member</h3>
            <Button 
              icon="close" 
              variant="text" 
              size="sm" 
              onClick={toggleAddMemberPanel} 
              className="!text-gray-500" 
            />
          </div>

          <div className="p-1 border-b border-gray-100 bg-gray-50 rounded-t-lg mb-1">
            <p className="text-xs text-gray-500 mb-1">
              Add {type} to Incentive List
            </p>
            <input
              type="text"
              placeholder="Search by Name"
              className="w-full p-1 border border-gray-300 rounded-md text-sm outline-none focus:border-orange-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col gap-1 p-1 max-h-[40vh] md:max-h-[50vh]">
            {loadingMembers ? (
              <div className="text-center text-gray-400 text-sm py-4">Loading candidates...</div>
            ) : availableMembers.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-4">No eligible employees found</div>
            ) : (
              availableMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded-md cursor-pointer"
                  onClick={() => handleToggleMember(member.id)}
                >
                  <input
                    type="checkbox"
                    className="accent-orange-500 w-3.5 h-3.5 cursor-pointer"
                    checked={selectedNewMembers.includes(member.id)}
                    onChange={() => {}}
                  />
                  <span className="text-[13px] font-medium text-gray-700 truncate">
                    {member.name}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className="flex-shrink-0 p-2 border-t border-gray-100 flex justify-end">
            <Button
              title={`Add Selected (${selectedNewMembers.length})`}
              className="bg-orange-500 text-white w-full"
              onClick={handleMoveMembers}
              disabled={selectedNewMembers.length === 0}
            />
          </div>
        </div>
      )}

      {/* Main Table */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="flex justify-between items-center mb-2 px-1">
          <h4 className="font-semibold text-gray-700">
            Existing {type} list
          </h4>
        </div>
        <TableAdv
          columns={columns}
          rows={rows}
          loading={loading}
          toolbarRight={toolbarRight}
          showCheckbox={false}
          serverPagination={true}
          totalCount={safeTotalCount}
          page={page}
          limit={rowsPerPage}
          onPaginationChange={handlePaginationChange}
          onLimitChange={handleLimitChange}
        />
      </div>

      <Alert
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null, name: "" })}
        title="Remove Member"
        description={`Are you sure you want to remove "${deleteModal.name}" from the ${type} incentive list? This action cannot be undone.`}
        acceptButton={{
          show: true,
          title: "Remove",
          onClick: handleDelete,
        }}
        cancelButton={{
          show: true,
          title: "Cancel",
          onClick: () => {},
        }}
      />
    </div>
  );
});

export default React.memo(TableIncentive);
